import {
  appearancePreferencesSchema,
  appearanceThemePatchSchema,
  colourBlindModePatchSchema,
  type AppearancePreferences,
  type AppearanceTheme,
} from '@molt/shared';
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { get, patch } from './api';
import { queryKeys } from './query-keys';

export function getAppearancePreferences(): Promise<AppearancePreferences> {
  return get('/me/appearance-preferences', appearancePreferencesSchema);
}

export function setAppearanceTheme(
  theme: AppearanceTheme,
): Promise<AppearancePreferences> {
  return patch(
    '/me/appearance-preferences/theme',
    appearancePreferencesSchema,
    appearanceThemePatchSchema.parse({ theme }),
  );
}

export function setColourBlindMode(
  colourBlindMode: boolean,
): Promise<AppearancePreferences> {
  return patch(
    '/me/appearance-preferences/colour-blind-mode',
    appearancePreferencesSchema,
    colourBlindModePatchSchema.parse({ colourBlindMode }),
  );
}

export function appearancePreferencesQueryOptions(): UseQueryOptions<
  AppearancePreferences,
  unknown
> {
  return {
    queryKey: queryKeys.meAppearance(),
    queryFn: getAppearancePreferences,
  };
}

export function useSetAppearanceTheme(): UseMutationResult<
  AppearancePreferences,
  unknown,
  AppearanceTheme
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setAppearanceTheme,
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.meAppearance(), preferences);
    },
  });
}

export function useSetColourBlindMode(): UseMutationResult<
  AppearancePreferences,
  unknown,
  boolean
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setColourBlindMode,
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.meAppearance(), preferences);
    },
  });
}
